import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge, FileLink } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ThermometerSnowflake } from "lucide-react";

function fmt(dtiso) {
  if (!dtiso) return "—";
  try { return new Date(dtiso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return dtiso; }
}

export default function ProtokollePage() {
  const [rooms, setRooms] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ date_from: "", date_to: "", room_id: "", type: "" });

  const load = async () => {
    setLoading(true);
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/logs", { params });
    setRows(data);
    setLoading(false);
  };

  useEffect(() => { api.get("/rooms").then((r) => setRooms(r.data)); }, []);
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader title="Protokolle" subtitle="Verlauf aller erledigten Tätigkeiten und Temperatureinträge" />

      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5 items-end">
        <div className="space-y-1"><Label className="text-xs">Von</Label>
          <Input type="date" data-testid="filter-date-from" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></div>
        <div className="space-y-1"><Label className="text-xs">Bis</Label>
          <Input type="date" data-testid="filter-date-to" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></div>
        <div className="space-y-1"><Label className="text-xs">Bereich</Label>
          <Select value={f.room_id || "all"} onValueChange={(v) => setF({ ...f, room_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-room"><SelectValue placeholder="Alle" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Bereiche</SelectItem>
              {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select></div>
        <div className="space-y-1"><Label className="text-xs">Art</Label>
          <Select value={f.type || "all"} onValueChange={(v) => setF({ ...f, type: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Alle" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="temperature">Temperatur</SelectItem>
              <SelectItem value="cleaning">Reinigung</SelectItem>
              <SelectItem value="generic">Prüfung</SelectItem>
            </SelectContent>
          </Select></div>
        <Button data-testid="btn-apply-filter" onClick={load}>Filtern</Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Laden …</div>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-2">{rows.length} Einträge</p>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="logs-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Datum/Zeit</th>
                  <th className="px-4 py-3">Bereich</th>
                  <th className="px-4 py-3">Objekt</th>
                  <th className="px-4 py-3">Tätigkeit</th>
                  <th className="px-4 py-3">Temp.</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Foto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.slice(0, 500).map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 whitespace-nowrap text-slate-600">{fmt(c.completed_at)}</td>
                    <td className="px-4 py-2.5">{c.room_name}</td>
                    <td className="px-4 py-2.5 text-slate-600">{c.object_name}</td>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{c.task_name}</td>
                    <td className="px-4 py-2.5 font-mono">{c.temperature != null ? `${c.temperature}°C` : "—"}</td>
                    <td className="px-4 py-2.5">
                      {c.exceeded ? <StatusBadge variant="warning">Grenzwert</StatusBadge>
                        : c.is_spontan ? <StatusBadge variant="teal">spontan</StatusBadge>
                        : <StatusBadge variant="done">OK</StatusBadge>}
                    </td>
                    <td className="px-4 py-2.5"><FileLink fileId={c.photo_file_id} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 500 && <p className="text-xs text-slate-400 mt-2">Zeige erste 500 – bitte Filter/PDF-Export nutzen.</p>}
        </>
      )}
    </div>
  );
}
